// src/domains/dash.roles.ts —— 06 C-22 权限与角色原生 renderVals（超管专属；经 domains/dash.ts 分派器挂载）。
// enter：GET /roles → 默认选中首个角色 → GET /roles/{id}/permissions；矩阵逐格即存（模板无保存按钮，计划 §6-P10）：
// 本地翻转后整表 PUT /roles/{id}/permissions {modules:[16 行]}，在途锁 perm:{roleId}（在途期间同角色其他格点击忽略），失败回滚本格并 toast。
// builtin=true 的角色（Super admin，后端按角色名判定，TD-717）格子置灰不可点，服务端 1012 兜底。角色改名无入口（§6-P7）。
// 数据存模块私有 WeakMap<host, RolesRun>；reset 换代即丢弃在途结果（session:lost / ready / 同页重复点击由分派器调用）。
import { ApiError } from '../../../shared/src/api/client';
import type { Dict } from '../../../shared/src/dc';
import { MODULES, type Module } from '../perm';
import type { RolePerm, RoleRow } from '../pages/platform.types';
import type { DashPart } from './dash';
import { openConfirm, openForm, sessionGuard } from './table';
import { str, type AdminCtx } from './types';

const EMPTY = '—';
const BITS = ['view', 'edit', 'approve', 'export'] as const;
type BitKey = (typeof BITS)[number];

// 仅部分位有意义的模块（后端 policy.Applicable；对其余位置 true → 4220 field=modules）：cardSecret 只有 view（2026-10-17），
// profit 只有 view / export（0047 利润台账只读）。非适用格渲染「—」不可点，整表 PUT 时恒为 false
const APPLICABLE: Partial<Readonly<Record<Module, readonly BitKey[]>>> = { cardSecret: ['view'], profit: ['view', 'export'] };
export const bitApplies = (m: Module, b: BitKey): boolean => APPLICABLE[m]?.includes(b) ?? true;
const sanitize = (r: RolePerm): RolePerm => {
  const m = r.module;
  if (!APPLICABLE[m]) return r;
  return { module: m, view: bitApplies(m, 'view') && r.view, edit: bitApplies(m, 'edit') && r.edit, approve: bitApplies(m, 'approve') && r.approve, export: bitApplies(m, 'export') && r.export };
};

interface RolesRun {
  roles: readonly RoleRow[] | null;
  sel: number | null; // 选中角色 id
  perms: Map<number, readonly RolePerm[]>;
  locks: Set<string>; // perm:{roleId}
}

const runs = new WeakMap<object, RolesRun>();

function fresh(): RolesRun {
  return { roles: null, sel: null, perms: new Map(), locks: new Set() };
}

function runOf(host: object): RolesRun {
  let r = runs.get(host);
  if (!r) {
    r = fresh();
    runs.set(host, r);
  }
  return r;
}

// 触发重渲染：ds.rev 自增（state.ts DashSlice）
function bump(ctx: AdminCtx): void {
  const ds = ctx.host.state.ds;
  ctx.host.setState({ ds: { rev: ds.rev + 1 } });
}

// 异步回调里出提示：以实时字典为准（分派器传入的 ctx.t 为挂载时字典，语言可能已切换）
function dictOf(ctx: AdminCtx): Dict {
  return ctx.gp.ui.dict();
}

function isPerm(v: unknown): v is RolePerm {
  if (typeof v !== 'object' || v === null) return false;
  return typeof Reflect.get(v, 'module') === 'string' && BITS.every((b) => typeof Reflect.get(v, b) === 'boolean');
}

// GET /roles/{id}/permissions 响应 {modules:[...]}：按 MODULES 顺序补齐 16 行（缺行按全 0，与后端 bitsOf 同口径）
function normalize(data: unknown): readonly RolePerm[] {
  const list: unknown = typeof data === 'object' && data !== null ? Reflect.get(data, 'modules') : null;
  const rows = Array.isArray(list) ? list.filter(isPerm) : [];
  return MODULES.map((m) => sanitize(rows.find((r) => r.module === m) ?? { module: m, view: false, edit: false, approve: false, export: false }));
}

function selected(run: RolesRun): RoleRow | null {
  return run.roles?.find((r) => r.id === run.sel) ?? null;
}

async function loadPerms(ctx: AdminCtx, run: RolesRun, id: number): Promise<void> {
  const data = await ctx.gp.api.call<unknown>('GET /api/admin/roles/{id}/permissions', { path: { id } });
  if (runs.get(ctx.host) !== run) return;
  run.perms.set(id, normalize(data));
  bump(ctx);
}

// 拉角色列表；prefer = 刷新后希望选中的角色（新增后选中新角色，删除后回落首个）
async function loadRoles(ctx: AdminCtx, run: RolesRun, prefer: number | null): Promise<void> {
  const list = await ctx.gp.api.call<RoleRow[]>('GET /api/admin/roles');
  if (runs.get(ctx.host) !== run) return;
  run.roles = Array.isArray(list) ? list : [];
  const keep = prefer ?? run.sel;
  run.sel = run.roles.find((r) => r.id === keep)?.id ?? run.roles[0]?.id ?? null;
  bump(ctx);
  if (run.sel !== null) await loadPerms(ctx, run, run.sel);
}

function report(ctx: AdminCtx, run: RolesRun, e: unknown): void {
  if (runs.get(ctx.host) === run) ctx.gp.ui.toast(ctx.gp.ui.err(e));
}

function refresh(ctx: AdminCtx, prefer: number | null): void {
  const run = runOf(ctx.host);
  void loadRoles(ctx, run, prefer).catch((e: unknown) => report(ctx, run, e));
}

function pick(ctx: AdminCtx, id: number): void {
  const run = runOf(ctx.host);
  if (run.sel === id) return;
  run.sel = id;
  bump(ctx);
  if (!run.perms.has(id)) void loadPerms(ctx, run, id).catch((e: unknown) => report(ctx, run, e));
}

// 1012 data.reason → 页文案（D7-13）
function roleErrText(d: Dict, e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.code === 1012) {
    if (e.data.reason === 'role_in_use') return str(d, 'errRoleInUse');
    if (e.data.reason === 'super_admin') return str(d, 'admErrSuperLocked');
  }
  return fallback;
}

// 逐格即存：在途锁 → 本地翻转 → 整表 PUT；失败回滚本格（只回滚该位，锁保证期间无其他改动）
async function toggleCell(ctx: AdminCtx, role: RoleRow, m: Module, bit: BitKey): Promise<void> {
  if (role.builtin || !bitApplies(m, bit)) return;
  const run = runOf(ctx.host);
  const lock = `perm:${role.id}`;
  const cur = run.perms.get(role.id);
  if (!cur || run.locks.has(lock)) return;
  run.locks.add(lock);
  const flip = (rows: readonly RolePerm[]): readonly RolePerm[] => rows.map((r) => (r.module === m ? { ...r, [bit]: !r[bit] } : r));
  const next = flip(cur);
  run.perms.set(role.id, next);
  bump(ctx);
  try {
    await ctx.gp.api.call('PUT /api/admin/roles/{id}/permissions', { path: { id: role.id }, body: { modules: next } });
  } catch (e) {
    if (runs.get(ctx.host) !== run) return;
    const now = run.perms.get(role.id);
    if (now) run.perms.set(role.id, flip(now));
    bump(ctx);
    const d = dictOf(ctx);
    ctx.gp.ui.toast(roleErrText(d, e, ctx.gp.ui.err(e)));
  } finally {
    run.locks.delete(lock);
  }
}

function addRole(ctx: AdminCtx): void {
  const t = ctx.t;
  openForm(ctx, {
    title: `${str(t, 'add')} · ${str(t, 'roles')}`,
    // 06 C-22 表单：成员数为派生计数，只读不提交
    fields: [
      { k: 'name', label: str(t, 'roleName'), type: 'text' },
      { k: 'members', label: str(t, 'cMembers'), type: 'readonly' },
    ],
    init: { name: '', members: '0' },
    submit: async (c, v) => {
      const name = typeof v['name'] === 'string' ? v['name'].trim() : '';
      const alive = sessionGuard(c);
      const row = await c.gp.api.call<RoleRow>('POST /api/admin/roles', { body: { name } });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastCreated'));
      refresh(c, row.id);
    },
  });
}

function delRole(ctx: AdminCtx): void {
  const t = ctx.t;
  const role = selected(runOf(ctx.host));
  if (!role) return;
  if (role.builtin) {
    ctx.gp.ui.toast(str(t, 'admErrSuperLocked'));
    return;
  }
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delText'),
    label: role.name,
    run: async (c) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call('DELETE /api/admin/roles/{id}', { path: { id: role.id } });
      } catch (e) {
        if (!alive()) return;
        const msg = roleErrText(c.t, e, '');
        if (!msg) throw e;
        c.gp.ui.toast(msg);
        return;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastDeleted'));
      const run = runOf(c.host);
      run.perms.delete(role.id);
      refresh(c, null);
    },
  });
}

// 原型 L855 权限格样式串逐字（勾选绿底 ✓）；内置角色置灰（opacity .5、not-allowed，与权限位置灰同形）
function cellStyle(on: boolean, locked: boolean): string {
  const base = `width:26px;height:26px;border-radius:7px;border:1px solid ${on ? '#0d9a68' : '#dfe5e2'};background:${on ? '#0d9a68' : '#fff'};color:#fff;cursor:pointer;font-size:12px;font-weight:800`;
  return locked ? `${base};opacity:.5;cursor:not-allowed` : base;
}

// 非适用格：与权限格同尺寸的无边框灰字占位，不可点
const NA_STYLE = 'width:26px;height:26px;border:0;background:transparent;color:#b5bfba;cursor:default;font-size:12px;font-weight:800';

// 原型 L963 角色卡样式串逐字（选中黑底反色）
function roleStyle(on: boolean): string {
  return `width:100%;text-align:left;padding:14px 16px;border:0;border-bottom:1px solid #f0f3f1;cursor:pointer;background:${on ? '#0f1a17' : '#fff'};color:${on ? '#fff' : '#0f1a17'}`;
}

function vals(ctx: AdminCtx): Record<string, unknown> {
  const t = ctx.t;
  const run = runOf(ctx.host);
  const role = selected(run);
  const perms = role ? run.perms.get(role.id) : undefined;
  return {
    roles: (run.roles ?? []).map((r) => ({ name: r.name, members: r.admin_count, pick: () => pick(ctx, r.id), style: roleStyle(r.id === run.sel) })),
    curRoleName: role?.name ?? EMPTY,
    permRows: MODULES.map((m) => {
      const p = perms?.find((x) => x.module === m);
      return {
        label: str(t, m),
        cells: BITS.map((b) => {
          if (!bitApplies(m, b)) return { mark: EMPTY, toggle: () => undefined, style: NA_STYLE };
          const on = p?.[b] ?? false;
          const locked = !role || !perms || role.builtin;
          return { mark: on ? '✓' : '', toggle: () => (role && !locked ? void toggleCell(ctx, role, m, b) : undefined), style: cellStyle(on, locked) };
        }),
      };
    }),
    addRole: () => addRole(ctx),
    delRole: () => delRole(ctx),
  };
}

export const DASH_ROLES: DashPart = {
  vals,
  enter: (ctx) => {
    // 每次进入重取（角色成员数 / 矩阵可能已被他人改动）；换代丢弃上一轮在途
    const run = fresh();
    runs.set(ctx.host, run);
    void loadRoles(ctx, run, null).catch((e: unknown) => report(ctx, run, e));
  },
  reset: (host) => {
    runs.set(host, fresh());
  },
};
